import React from 'react';

import {
  ApolloError,
  WatchQueryFetchPolicy
} from '@apollo/client';

import {
  errorUtil,
  paginationUtil,
  sortUtil
} from '../layer0';
import {
  queryWithOptionsUtil
} from '../layer1';



export interface IQueryBaseVariables<TGqlFilter, TGqlSort> {
  page?: string;
  filter?: TGqlFilter;
  sort?: TGqlSort;
}

export interface ITuple<TGqlResultData> {
  called: boolean;
  error?: ApolloError;
  loading: boolean;
  refetch?: () => void;
  data: TGqlResultData;
}

export interface IGqlSort<TGqlSortField extends string> {
  field: TGqlSortField;
  direction: sortUtil.ISortDirection;
}

export type FnUseLazyQuery<TGqlFilter, TGqlSort, TGqlResultData> = (params: {
  fetchPolicy: WatchQueryFetchPolicy;
  notifyOnNetworkStatusChange: boolean;
}) => readonly [
  (params: {
    variables: IQueryBaseVariables<TGqlFilter, TGqlSort>;
  }) => void,
  ITuple<TGqlResultData>
];

export function genUseListQuery<TClientSortColumnId extends string>() {
  function useListQuery<
    TClientFilter,
    TGqlFilter,
    TGqlSortField extends string,
    TDefaultFilterArgs,
    TGqlResultData,
    TWrappedTupleData
  >({
    useLazyQuery, genFilter2GqlFilter, defaultFilterArgs, clientColumn2GqlSortField, gqlResultData2GqlPageInfo, gqlResultData2TupleData, initQuerySort
  } : {
    useLazyQuery: FnUseLazyQuery<TGqlFilter, IGqlSort<TGqlSortField>, TGqlResultData>;
    genFilter2GqlFilter: (defaultFilterArgs: TDefaultFilterArgs) =>
      ((filter?: TClientFilter) => TGqlFilter);
    defaultFilterArgs: TDefaultFilterArgs;
    clientColumn2GqlSortField: Partial<Record<TClientSortColumnId, TGqlSortField>>;
    gqlResultData2GqlPageInfo: (gqlResultData: TGqlResultData) => paginationUtil.IGqlPageInfo | undefined;
    gqlResultData2TupleData: (gqlTuple: ITuple<TGqlResultData>) => TWrappedTupleData;
    initQuerySort?: sortUtil.ISortOptions<TClientSortColumnId>;
  }) {
    const filter2GqlFilter = React.useMemo(
      () => genFilter2GqlFilter(defaultFilterArgs),
      [
        defaultFilterArgs,
        genFilter2GqlFilter
      ]
    );



    function sort2GqlSort(sort?: sortUtil.ISortOptions<TClientSortColumnId>): IGqlSort<TGqlSortField> | undefined {
      const gqlFieldName = sort?.columnId ?
        (clientColumn2GqlSortField[sort.columnId] ?? undefined) :
        undefined;

      return sort && gqlFieldName ? {
        field: gqlFieldName,
        direction: sort.direction
      } : undefined;
    }



    const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
      useLazyQuery({
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true
      }));

    const [fnWrappedQuery, filterState, sortState] = queryWithOptionsUtil.useWrapLazyQueryWithOptions<TClientFilter, TGqlFilter, IGqlSort<TGqlSortField>, TClientSortColumnId>({
      fnQueryWithOptions: ({filter, sort}) =>
        fnQuery({
          variables: {
            filter,
            sort
          }
        }),
      fnClientFilter2Gql: filter2GqlFilter,
      fnClientSort2Gql: sort2GqlSort
    });

    const [fnQueryPage, pageInfo] = paginationUtil.usePaginatedQuery({
      fnQueryPage: page =>
        fnQuery({
          variables: {
            page,
            filter: filter2GqlFilter(filterState),
            sort: sort2GqlSort(sortState)
          }
        }),
      gqlPageInfo: gqlResultData2GqlPageInfo(tuple.data),
      wasCalled: tuple.called,
      disableInitQuery: !!initQuerySort
    });



    React.useEffect(
      () => {
        if (initQuerySort && !tuple.called && !tuple.loading) {
          fnWrappedQuery({
            sort: initQuerySort
          });
        }
      },
      [
        initQuerySort,
        tuple,
        fnWrappedQuery
      ]
    );



    const wrappedTuple = React.useMemo(
      () => ({
        ...tuple,
        data: gqlResultData2TupleData(tuple)
      }),
      [
        tuple,
        gqlResultData2TupleData
      ]
    );



    const gqlFilter = React.useMemo(
      () => filter2GqlFilter(filterState),
      [filterState]
    );



    const gqlSort = React.useMemo(
      () => sort2GqlSort(sortState),
      [sortState]
    );



    return {
      fnQuery: fnWrappedQuery,
      fnQueryPage,
      queryTuple: wrappedTuple,
      pageInfo,
      filterState,
      gqlFilter,
      gqlSort
    };
  }

  return useListQuery;
}
